import { API_BASE, authHeaders } from './apiAuth'
// One definition of what names a picture, shared by everything that takes one.
import type { ImageRef } from './imageRefs'

/**
 * The project canvas, and running a skill on it.
 *
 * Both go through the Worker. Running could have been faked in the browser —
 * this version returns its input unchanged — but then the part that actually
 * causes trouble would be untested: an authenticated request, a timeout, a
 * failure that has to be drawn somewhere, a result that has to be stored. All
 * of those would have arrived at once on the day a real skill landed.
 */

/** What is on the canvas, as it is stored. Deliberately small and flat. */
export interface CanvasDocument {
  nodes: CanvasNode[]
  edges: CanvasEdge[]
  /**
   * Sheets of the same board.
   *
   * A project's pictures, files and runs are not divided by this — a page is a
   * view, not a container. Which is the whole point: a reference dragged onto
   * page two is the same picture it was on page one, and a skill card can be
   * copied between them without anything being moved or re-uploaded.
   *
   * Absent on every board made before this existed, which is why it is
   * optional: those have one page, and saying so is the loader's job rather
   * than a migration's.
   */
  pages?: CanvasPage[]
}

export interface CanvasPage {
  id: string
  name: string
  /**
   * A workflow's own page (canvas/workflow, 2026-10-16): the card it belongs
   * to. Not among the page tabs; opened from the card.
   */
  workflow?: string
}

export interface CanvasNode {
  id: string
  type: 'image' | 'skill' | 'picture' | 'upscale' | 'video' | 'angle' | 'vector' | 'grade' | 'moodboard' | 'workflow' | 'frame' | 'note' | 'edit' | 'text' | 'label' | 'library' | 'background' | 'eraser'
  /** Which sheet it is on. Missing means the first one, for boards made before pages. */
  page?: string
  position: { x: number; y: number }
  width?: number
  height?: number
  data: Record<string, unknown>
}

export interface CanvasEdge {
  id: string
  source: string
  target: string
  /**
   * Which port on the target, when the target has more than one.
   *
   * Every node used to have exactly one input called `in`, so the name could
   * be assumed on the way back in. A card that makes pictures has a numbered
   * port per reference and no port called `in` at all — and an edge whose
   * handle is assumed rather than stored comes back attached to nothing, which
   * React Flow draws from the node's top-left corner.
   */
  targetHandle?: string
}

export const EMPTY_CANVAS: CanvasDocument = { nodes: [], edges: [], pages: [] }

async function reason(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string }
    if (typeof body.error === 'string' && body.error) return body.error
  } catch {
    /* fall through to the status */
  }
  return `Something went wrong (HTTP ${response.status})`
}

export async function loadCanvas(projectId: string): Promise<CanvasDocument> {
  const response = await fetch(`${API_BASE}/api/canvas?id=${encodeURIComponent(projectId)}`, {
    headers: { Accept: 'application/json', ...(await authHeaders()) },
  })
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as Partial<CanvasDocument>
  return {
    nodes: Array.isArray(body.nodes) ? body.nodes : [],
    edges: Array.isArray(body.edges) ? body.edges : [],
    pages: Array.isArray(body.pages) ? body.pages : [],
  }
}

export async function saveCanvas(projectId: string, canvas: CanvasDocument): Promise<void> {
  const response = await fetch(`${API_BASE}/api/canvas?id=${encodeURIComponent(projectId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(canvas),
  })
  if (!response.ok) throw new Error(await reason(response))
}

export interface RunResult {
  skill: string
  outputs: ImageRef[]
  /** True while a skill is a stand-in that hands back what it was given. */
  passthrough: boolean
}

export async function runSkill(
  skillRef: string,
  inputs: ImageRef[],
  projectId: string,
): Promise<RunResult> {
  const response = await fetch(`${API_BASE}/api/skills/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ skill: skillRef, inputs, projectId }),
  })
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as Partial<RunResult>
  return {
    skill: body.skill ?? skillRef,
    outputs: Array.isArray(body.outputs) ? body.outputs : [],
    passthrough: body.passthrough === true,
  }
}
